import { AdSlotType } from '../../advertising/AdTypes';
import type { AssetId } from '../../assets/AssetTypes';
import { DEG_TO_RAD } from '../../utils/math';
import { ROADSIDE_TAG } from '../segments/segmentBuilders';

/**
 * Space a large backdrop decoration claims in the world (meters, ground plane). Backdrop pieces
 * stand far from their own segment and can reach the neighbours of a turn – the TrackPresenter
 * skips a piece whose circle overlaps one that is already standing.
 */
export interface DecorationSpacing {
  /** Ground circle around the origin (half the footprint diagonal for buildings). */
  readonly radius: number;
  /** Minimum distance to another placement of the same asset (crane jibs must not cross). */
  readonly sameAssetDistance?: number;
}

/** Decorations are visual only (no collision) and fill decoration sockets. */
export interface DecorationDefinition {
  readonly assetId: AssetId;
  /** Footprint must fit the socket's maxFootprint. */
  readonly footprint: { readonly width: number; readonly depth: number };
  /** The socket must carry all of these tags. */
  readonly requiredSocketTags: readonly string[];
  readonly weight: number;
  /** Fixed yaw added to the socket yaw (radians) – e.g. a model whose main axis is +z where the socket expects +x. */
  readonly yaw?: number;
  /** Random yaw added to the socket yaw, ± radians (cosmetic RNG). */
  readonly yawJitter?: number;
  readonly spacing?: DecorationSpacing;
  /**
   * Ad surfaces carried by the model (e.g. the banner on a roadside scaffold) – placed with the decoration and run like
   * the segment's own slots (AdSlotBinder). Position in the model's own frame (front +z), facing +z.
   */
  readonly advertisingSlots?: readonly DecorationAdSlot[];
}

export interface DecorationAdSlot {
  readonly id: string;
  readonly slotType: AdSlotType;
  /** Centre of the visible surface in the model frame (x along the façade, y up, z towards the front). */
  readonly offset: { readonly x: number; readonly y: number; readonly z: number };
  readonly width: number;
  readonly height: number;
  readonly fillRate?: number;
}

/**
 * A decoration's ad surface as a slot of its segment (path coordinates like the segment's own slots): the model offset turned
 * by the decoration's yaw (socket yaw + placement offset – the same turn TrackPresenter gives the model, Babylon y-rotation),
 * the surface facing the model front. Slot ids are unique per socket.
 */
export function decorationAdSlot(
  socket: { readonly id: string; readonly position: { readonly x: number; readonly y: number; readonly z: number }; readonly yaw: number },
  yawOffset: number,
  slot: DecorationAdSlot,
): { id: string; slotType: AdSlotType; position: { x: number; y: number; z: number }; yaw: number; width: number; height: number; fillRate?: number } {
  const yaw = socket.yaw + yawOffset;
  const cos = Math.cos(yaw);
  const sin = Math.sin(yaw);
  return {
    id: `${socket.id}:${slot.id}`,
    slotType: slot.slotType,
    position: {
      x: socket.position.x + slot.offset.x * cos + slot.offset.z * sin,
      y: socket.position.y + slot.offset.y,
      z: socket.position.z - slot.offset.x * sin + slot.offset.z * cos,
    },
    yaw,
    width: slot.width,
    height: slot.height,
    ...(slot.fillRate === undefined ? {} : { fillRate: slot.fillRate }),
  };
}

/**
 * Banner on the netting of the roadside scaffold (ructec 2026-10-05: "den Gerüstbanner so umbauen, dass er gleich groß ist
 * wie der Banner am Zaun"): once baked into ENV_RoadsideScaffold_A (6 × 2 m, ructec only), now an ad surface in the fence
 * format – 6 × 1.5 m, 4:1, the same poster as the fence banners. Where the baked one hung: centre 0.4 m along the façade,
 * 5.5 m up, just in front of the net (roadside_parts.py BANNER, z −0.72).
 */
const SCAFFOLD_NET_BANNER: DecorationAdSlot = {
  id: 'AD_SCAFFOLD_NET',
  slotType: AdSlotType.FenceLeft,
  offset: { x: 0.4, y: 5.5, z: -0.69 },
  width: 6,
  height: 1.5,
  fillRate: 1,
};

/** Roadside bay assets fill a whole bay: 10 m along the track (local x) × 7 m across (local z, front +z). */
const ROADSIDE_BAY_FOOTPRINT = { width: 10, depth: 7 } as const;
/**
 * Bays claim ground like the backdrop pieces, so a bay never stands inside a building of another
 * segment around a junction. Inner circle of the bay, kept below 3.0 m: the segment's own building
 * (radius 9.9) stands 12.9 m from each bay centre.
 */
const ROADSIDE_BAY_SPACING: DecorationSpacing = { radius: 2.9 };

export const DEFAULT_DECORATIONS: readonly DecorationDefinition[] = [
  { assetId: 'ENV_CONTAINER_A', footprint: { width: 6.06, depth: 2.44 }, requiredSocketTags: ['ground'], weight: 2 },
  { assetId: 'ENV_LIGHT_A', footprint: { width: 1.25, depth: 1.25 }, requiredSocketTags: ['small'], weight: 3 },
  { assetId: 'ENV_CABLE_REEL_A', footprint: { width: 1.14, depth: 1.77 }, requiredSocketTags: ['ground'], weight: 1 },
  { assetId: 'PROP_PIPE_STACK_A', footprint: { width: 2.2, depth: 3.6 }, requiredSocketTags: ['ground'], weight: 2 },
  { assetId: 'PROP_PALLET_GROUP_A', footprint: { width: 2.8, depth: 1.8 }, requiredSocketTags: ['ground'], weight: 3 },
  { assetId: 'ENV_SCAFFOLD_TOWER_A', footprint: { width: 2.72, depth: 1.64 }, requiredSocketTags: ['ground'], weight: 2 },
  // Night-ready site dressing (Blender 2026-10-02). Their fronts face the model's +z; sockets point +x towards the track.
  { assetId: 'ENV_LIGHT_MAST_A', footprint: { width: 2.5, depth: 2.06 }, requiredSocketTags: ['ground'], weight: 1.2, yaw: 90 * DEG_TO_RAD, yawJitter: 15 * DEG_TO_RAD },
  { assetId: 'ENV_GENERATOR_A', footprint: { width: 2.2, depth: 1.0 }, requiredSocketTags: ['ground'], weight: 1, yaw: 90 * DEG_TO_RAD, yawJitter: 10 * DEG_TO_RAD },
  // parked forklift, forks towards the road (design/02_vehicles: VEH_03_Forklift)
  { assetId: 'VEH_FORKLIFT_A', footprint: { width: 3.6, depth: 1.64 }, requiredSocketTags: ['ground'], weight: 0.8, yaw: 90 * DEG_TO_RAD, yawJitter: 25 * DEG_TO_RAD },
  { assetId: 'ENV_WARNING_LIGHT_A', footprint: { width: 0.45, depth: 0.45 }, requiredSocketTags: ['small'], weight: 2 },
  { assetId: 'ENV_SKIP_CONTAINER_A', footprint: { width: 2.12, depth: 3.62 }, requiredSocketTags: ['ground'], weight: 1.2, yawJitter: 12 * DEG_TO_RAD },
  { assetId: 'ENV_REBAR_BUNDLE_A', footprint: { width: 6.3, depth: 1.5 }, requiredSocketTags: ['ground'], weight: 1, yawJitter: 4 * DEG_TO_RAD },
  // Roadside bays (reference sheets): dense site dressing right behind the fence – always parallel to the track (no jitter).
  // scaffolded façade with netting and banner
  { assetId: 'ENV_ROADSIDE_SCAFFOLD_A', footprint: ROADSIDE_BAY_FOOTPRINT, requiredSocketTags: [ROADSIDE_TAG], weight: 1.2, spacing: ROADSIDE_BAY_SPACING, advertisingSlots: [SCAFFOLD_NET_BANNER] },
  // blue containers stacked two high
  { assetId: 'ENV_ROADSIDE_CONTAINERS_A', footprint: ROADSIDE_BAY_FOOTPRINT, requiredSocketTags: [ROADSIDE_TAG], weight: 1, spacing: ROADSIDE_BAY_SPACING },
  // material yard: pallets, pipes, blocks, rebar
  { assetId: 'ENV_ROADSIDE_YARD_A', footprint: ROADSIDE_BAY_FOOTPRINT, requiredSocketTags: [ROADSIDE_TAG], weight: 1, spacing: ROADSIDE_BAY_SPACING },
  // yard with light mast and generator – preferred in night zones (zones.config: nightDecorationWeights)
  { assetId: 'ENV_ROADSIDE_YARD_LIT_A', footprint: ROADSIDE_BAY_FOOTPRINT, requiredSocketTags: [ROADSIDE_TAG], weight: 0.6, spacing: ROADSIDE_BAY_SPACING },
  // stacked site-office containers
  { assetId: 'ENV_ROADSIDE_OFFICE_A', footprint: ROADSIDE_BAY_FOOTPRINT, requiredSocketTags: [ROADSIDE_TAG], weight: 0.7, spacing: ROADSIDE_BAY_SPACING },
  // Backdrop (every sheet): scaffolded shell buildings along the track, tower cranes behind them.
  {
    assetId: 'BLD_SHELL_A',
    footprint: { width: 18.04, depth: 13.05 },
    requiredSocketTags: ['backdrop'],
    weight: 1,
    // < half the segment length: buildings of neighbouring straights may stand side by side
    spacing: { radius: 9.9 },
  },
  {
    // excavator working beside the track, boom towards the road (design/02_vehicles/VEH_01_Excavator)
    assetId: 'VEH_EXCAVATOR_A',
    footprint: { width: 3.1, depth: 8.6 },
    requiredSocketTags: ['backdrop', 'yard'],
    weight: 0.5,
    yawJitter: 25 * DEG_TO_RAD,
    spacing: { radius: 4.6 },
  },
  {
    // parked tipper truck facing the road (design/02_vehicles/VEH_04_Truck)
    assetId: 'VEH_TRUCK_A',
    footprint: { width: 2.96, depth: 9.13 },
    requiredSocketTags: ['backdrop', 'yard'],
    weight: 0.4,
    yawJitter: 30 * DEG_TO_RAD,
    spacing: { radius: 4.8 },
  },
  {
    assetId: 'ENV_CRANE_A',
    footprint: { width: 4.5, depth: 4.97 },
    requiredSocketTags: ['backdrop', 'crane'],
    weight: 1,
    yawJitter: 40 * DEG_TO_RAD,
    // jib 51 m – two cranes closer than that could swing into each other
    spacing: { radius: 3.4, sameAssetDistance: 60 },
  },
  {
    // site crane of the sheets (design/02_vehicles/VEH_02_Crane): 24 m, jib swung over the track
    assetId: 'VEH_CRANE_A',
    footprint: { width: 6, depth: 6 },
    requiredSocketTags: ['backdrop', 'crane'],
    weight: 1,
    yaw: 90 * DEG_TO_RAD, // jib along the model's +z, crane sockets point +x across the track
    yawJitter: 35 * DEG_TO_RAD,
    // jib 30 m + counter-jib 12 m – two of them closer than that could swing into each other
    spacing: { radius: 3.4, sameAssetDistance: 44 },
  },
];
